// src/pages/medichat/DeliveryAddressModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import AlertModal from '../common/AlertModal';
import FormField from '../common/FormField';
import { useFormState } from '../../utils/useFormState';
import { deliveryAddressValidationRules, deliveryAddressSimpleValidationRules } from '../../utils/validationRules';
import './medichat_mypage.scss';

const blankFull = {
  label: '',
  recipient_name: '',
  postal_code: '',
  prefecture: '',
  city: '',
  address_line1: '',
  address_line2: '',
  phone_number: '',
  is_default: false,
};

// 新規登録用の簡易フォーム
const blankSimple = {
  postal_code: '',
  address: '',        // ← フルアドレス（都道府県+市区町村+番地まで）
  address_line2: '',  // 建物名・部屋番号
  is_default: false,
};

const DeliveryAddressModal = ({ open, onClose, onSaved, initialAddress }) => {
  const isEdit = !!initialAddress?.id;

  // バリデーションルールの選択（新規は簡易、編集は完全）
  const validationRules = isEdit ? deliveryAddressValidationRules : deliveryAddressSimpleValidationRules;
  const initialValues = isEdit ? { ...blankFull, ...(initialAddress || {}) } : { ...blankSimple };

  // ★ Hooksは常に呼ぶ（条件分岐の前）
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    canSubmit,
    setValues
  } = useFormState(initialValues, validationRules);

  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const [lastLookedUpCode, setLastLookedUpCode] = useState(''); // 検索済み郵便番号を記録

  useEffect(() => {
    if (!open) return;
    const newValues = isEdit ? { ...blankFull, ...(initialAddress || {}) } : { ...blankSimple };
    setValues(newValues);
    setLastLookedUpCode(''); // モーダル開いた時にリセット
  }, [open, isEdit, initialAddress, setValues]);

  const title = isEdit ? '住所を編集' : '新しい住所を登録';

  // --- 郵便番号自動補完（新規のみ） ---
  useEffect(() => {
    if (!open || isEdit || !values) return;
    const code = String(values.postal_code ?? '').replace(/\D/g, '');

    // 7桁でない、または既に検索済みの郵便番号ならスキップ
    if (code.length !== 7 || code === lastLookedUpCode) return;

    let cancelled = false;
    setLookingUp(true);

    axiosInstance
      .get('/api/medichat/postal_codes/lookup', { params: { code } })
      .then(({ data }) => {
        if (cancelled) return;
        const { prefecture = '', city = '', town = '' } = data || {};
        // 町域までで自動入力（番地はユーザー入力想定）
        const auto = [prefecture, city, town].filter(Boolean).join('');
        if (auto) {
          handleChange('address', auto);
        }
        // API成功後に検索済みとして記録
        setLastLookedUpCode(code);
      })
      .catch(() => {
        // 補完失敗時も検索済みとして記録（無限リトライを防ぐ）
        setLastLookedUpCode(code);
      })
      .finally(() => {
        if (!cancelled) setLookingUp(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, values?.postal_code]); // handleChangeとlastLookedUpCodeを依存配列から除外

  const submit = async (e) => {
    e?.preventDefault();
    
    // バリデーション実行
    const isValid = validateAll();
    if (!isValid) {
      setAlertModal({ open: true, title: '入力エラー', message: '入力内容を確認してください' });
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        // 既存編集：従来どおりカラムを送る
        const payload = { address: {
          label:            values.label ?? '',
          recipient_name:   values.recipient_name ?? '',
          postal_code:      values.postal_code ?? '',
          prefecture:       values.prefecture ?? '',
          city:             values.city ?? '',
          address_line1:    values.address_line1 ?? '',
          address_line2:    values.address_line2 ?? '',
          phone_number:     values.phone_number ?? '',
          is_default:       !!values.is_default,
        }};
        await axiosInstance.put(`/api/medichat/delivery_addresses/${initialAddress.id}`, payload);
      } else {
        // 新規：postal_code + address(フル) + address_line2 + is_default だけ
        const payload = { address: {
          postal_code:   values.postal_code ?? '',
          address:       values.address ?? '',
          address_line2: values.address_line2 ?? '',
          is_default:    !!values.is_default,
        }};
        await axiosInstance.post('/api/medichat/delivery_addresses', payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setAlertModal({ open: true, title: 'エラー', message: err?.response?.data?.message || '保存に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  // ★ Hooksの後にリターン
  if (!open) return null;
  
  // ★ valuesが初期化されていない場合は何も表示しない
  if (!values) return null;

  return (
    <div className="mypage-modal-backdrop">
      <div className="mypage-modal" role="dialog" aria-modal="true" aria-labelledby="addressModalTitle">
        <div className="mypage-modal__header">
          <h3 id="addressModalTitle" style={{margin:0}}>{title}</h3>
          <button className="mypage-modal__close" onClick={onClose} aria-label="閉じる">×</button>
        </div>

        <form className="mypage-modal-body" onSubmit={submit}>
          {!isEdit ? (
            <>
              <FormField
                label="郵便番号"
                name="postal_code"
                value={values.postal_code ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.postal_code}
                touched={touched.postal_code}
                required={true}
                placeholder="1500043"
                helperText="7桁・ハイフンなしで入力してください"
              />
              {lookingUp && <small style={{marginLeft:8,color:'#6b7280'}}>住所を検索中…</small>}
              
              <FormField
                label="住所"
                name="address"
                value={values.address ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.address}
                touched={touched.address}
                required={true}
                placeholder="東京都渋谷区道玄坂1-2-3"
                helperText="都道府県から番地まで入力してください"
              />
              
              <FormField
                label="建物名・部屋番号"
                name="address_line2"
                value={values.address_line2 ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.address_line2}
                touched={touched.address_line2}
                required={false}
                optional={true}
                placeholder="渋谷マンション101号室"
              />
            </>
          ) : (
            <>
              <FormField
                label="郵便番号"
                name="postal_code"
                value={values.postal_code ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.postal_code}
                touched={touched.postal_code}
                required={true}
                placeholder="1500043"
                helperText="7桁・ハイフンなしで入力してください"
              />
              
              <FormField
                label="都道府県"
                name="prefecture"
                value={values.prefecture ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.prefecture}
                touched={touched.prefecture}
                required={true}
              />
              
              <FormField
                label="市区町村"
                name="city"
                value={values.city ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.city}
                touched={touched.city}
                required={true}
                placeholder="渋谷区"
              />
              
              <FormField
                label="番地"
                name="address_line1"
                value={values.address_line1 ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.address_line1}
                touched={touched.address_line1}
                required={true}
                placeholder="道玄坂1-2-3"
              />
              
              <FormField
                label="建物名・部屋番号"
                name="address_line2"
                value={values.address_line2 ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.address_line2}
                touched={touched.address_line2}
                required={false}
                optional={true}
                placeholder="渋谷マンション101号室"
              />
            </>
          )}

          <div className="mypage-modal-actions">
            <button type="submit" className="mypage-btn-primary" disabled={saving}>
              {saving ? '保存中…' : (isEdit ? '更新' : '登録')}
            </button>
          </div>
        </form>
      </div>
      
      <AlertModal 
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
    </div>
  );
};

export default DeliveryAddressModal;
